import { useEffect, useState } from "react";
import { ArrowRightLeft, ExternalLink, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { eur } from "./docUtils";

const KIND = {
  devis: { label: "Devis", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  facture: { label: "Facture", cls: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  paiement: { label: "Paiement", cls: "bg-green-50 text-green-700 border-green-200" },
  avoir: { label: "Avoir", cls: "bg-red-50 text-red-700 border-red-200" },
};

function Row({ label, value, testId }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-neutral-400 w-40 shrink-0">{label}</span>
      <span className="font-medium break-words" data-testid={testId}>{value}</span>
    </div>
  );
}

export default function ClientPonctuelSheet({ client, onClose, onConverted }) {
  const navigate = useNavigate();
  const [fin, setFin] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (client?.id) api.get(`/accounting/client-financials/${client.id}`).then((r) => setFin(r.data)).catch(() => {});
  }, [client?.id]);

  if (!client) return null;
  const c = client;
  const contact = [c.contact_first_name, c.contact_last_name].filter(Boolean).join(" ");
  const address = [c.address, c.address2, [c.zip_code, c.city].filter(Boolean).join(" "), c.country].filter(Boolean).join(", ");

  const invoices = (fin?.history || []).filter((h) => h.kind === "facture");
  const paid = invoices.filter((h) => h.status === "payee").length;
  const unpaid = invoices.filter((h) => ["validee", "envoyee", "partiellement_payee"].includes(h.status)).length;
  const totalCA = invoices.reduce((s, h) => s + (h.amount || 0), 0);
  const last = invoices[0];

  const convert = async () => {
    setConverting(true);
    try {
      await api.patch(`/clients/${c.id}`, { kind: "recurrent" });
      toast.success(`${c.name} est maintenant un client récurrent — historique et factures conservés`);
      onConverted();
      onClose();
      navigate(`/clients/${c.id}?tab=params`);
    } catch {
      toast.error("Erreur lors de la conversion");
    } finally {
      setConverting(false);
    }
  };

  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ponctuel-sheet">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {c.name}
            <span className="text-[10px] font-semibold bg-neutral-100 border border-neutral-200 rounded-full px-2 py-0.5">Client non récurrent</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2" data-testid="ponctuel-coords">
            <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Coordonnées</p>
            <Row label={c.client_type === "particulier" ? "Nom" : "Raison sociale"} value={c.raison_sociale || c.name} testId="ponctuel-raison" />
            {c.trade_name ? <Row label="Nom commercial" value={c.trade_name} /> : null}
            <Row label="Contact" value={contact} />
            <Row label="E-mail" value={c.email} />
            <Row label="Téléphone" value={c.phone} />
            <Row label="Adresse de facturation" value={address || c.billing_address} testId="ponctuel-address" />
            <Row label="SIRET" value={c.billing_siret} />
            <Row label="TVA intracommunautaire" value={c.vat_number} />
            <Row label="Pays" value={c.country} />
          </div>
          <div className="space-y-2" data-testid="ponctuel-history">
            <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Historique</p>
            {!fin ? <p className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</p> : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { l: "Factures", v: invoices.length, t: "ponctuel-nb-invoices" },
                    { l: "Chiffre d'affaires", v: eur(totalCA), t: "ponctuel-ca" },
                    { l: "Factures payées", v: paid, t: "ponctuel-paid" },
                    { l: "Factures impayées", v: unpaid, t: "ponctuel-unpaid" },
                  ].map((k) => (
                    <div key={k.t} className="bg-neutral-50 border border-neutral-100 rounded-lg p-3" data-testid={k.t}>
                      <p className="text-[10px] uppercase tracking-wider text-neutral-400">{k.l}</p>
                      <p className="font-heading font-bold text-lg">{k.v}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-neutral-500">
                  {last ? `Dernière facture : ${last.label} (${last.date})` : "Aucune facture pour le moment."}
                </p>
              </>
            )}
          </div>
        </div>

        {fin && fin.history.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold mb-2">Historique complet des factures</p>
            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="ponctuel-full-history">
              {fin.history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-sm border border-neutral-100 rounded-lg px-3 py-2">
                  <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${KIND[h.kind].cls}`}>{KIND[h.kind].label}</span>
                  <span className="flex-1 min-w-0 truncate">{h.label}</span>
                  <span className="text-xs text-neutral-400 shrink-0">{h.date}</span>
                  <span className={`font-medium shrink-0 ${h.amount < 0 ? "text-red-600" : h.kind === "paiement" ? "text-green-700" : ""}`}>
                    {h.amount != null ? `${h.amount < 0 ? "-" : ""}${eur(Math.abs(h.amount))}` : "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-between pt-2">
          <Button variant="outline" onClick={() => navigate(`/comptabilite?client=${c.id}`)} className="gap-1.5" data-testid="ponctuel-goto-invoices">
            <ExternalLink className="w-4 h-4" /> Voir dans Comptabilité
          </Button>
          <Button onClick={convert} disabled={converting} className="bg-neutral-900 text-white gap-1.5" data-testid="ponctuel-convert">
            {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
            Passer en client récurrent
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
